import { useState, useEffect, useCallback } from 'react';
import { careerApplicationService } from '../services/apiService';
import { Eye, Trash2 } from 'lucide-react';

const CareerApplicationList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const data = await careerApplicationService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await careerApplicationService.getAllApplications(params);
      setApplications(data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchApplications();
    fetchStats();
  }, [fetchApplications, fetchStats]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await careerApplicationService.updateApplicationStatus(id, newStatus);
      fetchApplications();
      fetchStats();
      if (selectedApp && selectedApp._id === id) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    try {
      await careerApplicationService.deleteApplication(id);
      fetchApplications();
      fetchStats();
      if (selectedApp && selectedApp._id === id) {
        setSelectedApp(null);
      }
    } catch (err) {
      alert('Failed to delete application: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewing: 'bg-blue-100 text-blue-800',
      shortlisted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      hired: 'bg-purple-100 text-purple-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading && !applications.length) {
    return <div className="text-center py-8">Loading applications...</div>;
  }

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-medium text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg shadow-sm border border-yellow-200">
            <div className="text-2xl font-medium text-yellow-800">{stats.pending}</div>
            <div className="text-sm text-yellow-700">Pending</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-200">
            <div className="text-2xl font-medium text-blue-800">{stats.reviewing}</div>
            <div className="text-sm text-blue-700">Reviewing</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-200">
            <div className="text-2xl font-medium text-green-800">{stats.shortlisted}</div>
            <div className="text-sm text-green-700">Shortlisted</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg shadow-sm border border-red-200">
            <div className="text-2xl font-medium text-red-800">{stats.rejected}</div>
            <div className="text-sm text-red-700">Rejected</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg shadow-sm border border-purple-200">
            <div className="text-2xl font-medium text-purple-800">{stats.hired}</div>
            <div className="text-sm text-purple-700">Hired</div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['all', 'pending', 'reviewing', 'shortlisted', 'rejected', 'hired'].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === status
                ? 'bg-green-700 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No applications found
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr key={app._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{app.firstName} {app.lastName}</div>
                      <div className="text-xs text-gray-500">{app.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{app.positionTitle}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{app.experience}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(app.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedApp(app)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(app._id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Application Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h3 className="text-xl font-medium text-gray-900">
                  {selectedApp.firstName} {selectedApp.lastName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">{selectedApp.positionTitle}</p>
              </div>
              <button
                onClick={() => setSelectedApp(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Contact Info */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-600">Email:</span> {selectedApp.email}</div>
                    <div><span className="text-gray-600">Phone:</span> {selectedApp.phone}</div>
                    {selectedApp.linkedinUrl && (
                      <div>
                        <span className="text-gray-600">LinkedIn:</span>{' '}
                        <a href={selectedApp.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Profile
                        </a>
                      </div>
                    )}
                    {selectedApp.portfolioUrl && (
                      <div>
                        <span className="text-gray-600">Portfolio:</span>{' '}
                        <a href={selectedApp.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Portfolio
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Professional Info */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Professional Background</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-600">Experience:</span> {selectedApp.experience}</div>
                    <div><span className="text-gray-600">Education:</span> {selectedApp.education}</div>
                    {selectedApp.currentOrganization && (
                      <div><span className="text-gray-600">Current Org:</span> {selectedApp.currentOrganization}</div>
                    )}
                    {selectedApp.expectedSalary && (
                      <div><span className="text-gray-600">Expected Salary:</span> {selectedApp.expectedSalary}</div>
                    )}
                    {selectedApp.availability && (
                      <div><span className="text-gray-600">Availability:</span> {selectedApp.availability}</div>
                    )}
                  </div>
                </div>

                {/* Cover Letter */}
                {selectedApp.coverLetter && (
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-2">Cover Letter</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{selectedApp.coverLetter}</p>
                  </div>
                )}

                {/* Why Apply */}
                {selectedApp.whyApply && (
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-2">Why Apply</h4>
                    <p className="text-sm text-gray-700">{selectedApp.whyApply}</p>
                  </div>
                )}

                {/* Status Change */}
                <div className="md:col-span-2">
                  <h4 className="font-medium text-gray-900 mb-2">Change Status</h4>
                  <div className="flex gap-2 flex-wrap">
                    {['pending', 'reviewing', 'shortlisted', 'rejected', 'hired'].map(status => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(selectedApp._id, status)}
                        className={`px-4 py-2 rounded text-sm font-medium ${
                          selectedApp.status === status
                            ? 'bg-green-700 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Admin Notes */}
                <div className="md:col-span-2">
                  <h4 className="font-medium text-gray-900 mb-2">Admin Notes</h4>
                  <textarea
                    value={selectedApp.adminNotes || ''}
                    onChange={(e) => setSelectedApp(prev => ({ ...prev, adminNotes: e.target.value }))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Add notes about this application..."
                  />
                  <button
                    onClick={async () => {
                      try {
                        await careerApplicationService.updateAdminNotes(selectedApp._id, selectedApp.adminNotes);
                        alert('Notes updated successfully');
                      } catch (err) {
                        alert('Failed to update notes: ' + err.message);
                      }
                    }}
                    className="mt-2 px-4 py-2 bg-green-700 text-white rounded text-sm hover:bg-green-800"
                  >
                    Save Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerApplicationList;
